import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { AntibioticTreatmentSelect } from "./AntibioticTreatmentSelect";
import { Select, SelectItem, Input, Button } from "@heroui/react";
import "./EosForm.scss";

import alertCircleIcon from "../../../assets/alert-circle.svg";

// ...types stay the same

const labelStyle = {
  width: "100px",
  minWidth: "100px",
  textAlign: "right" as const,
  color: "#0C3058",
  alignSelf: "center",
};

// Prevent browser native validation tooltip ("Value must be...") while keeping min/max (arrows still work).
const suppressNativeInvalid = (e: React.FormEvent<HTMLInputElement>) => {
  e.preventDefault();
  e.currentTarget.setCustomValidity("");
};

// Returns true if value is invalid (when empty => false, so no error while blank).
const outOfRange = (value: number | undefined, min: number, max: number) => {
  if (value === undefined || value === null) return false;
  if (Number.isNaN(value)) return true;
  return value < min || value > max;
};

// Small helper to render error row with test-id support.
const FieldError: React.FC<{ show: boolean; message: string; testId?: string }> = ({
  show,
  message,
  testId,
}) => {
  if (!show) return <div style={{ height: "1rem" }} />;

  return (
    <div
      className="field-error"
      data-testid={testId}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        marginBottom: 10,
        height: "1rem",
      }}
    >
      <img
        src={alertCircleIcon}
        alt="Alert"
        style={{ width: "1rem", height: "1rem", marginRight: "4px" }}
      />
      <span className="error-message">{message.replace(/\s+/g, " ").trim()}</span>
    </div>
  );
};

export type AntibioticTreatment = "none" | "broad-4" | "broad-2" | "GBS-2";

export type TForm = Partial<{
  temperature?: number;
  pregnancyLengthWeeks?: number;
  pregnancyLengthDays?: number;
  rom?: number;
  gbs?: "positive" | "negative" | "unknown";
  antibioticTreatment?: AntibioticTreatment;
}>;

export type TErrors = Partial<Record<keyof TForm, boolean>>;

type Props = {
  form: TForm;
  errors: TErrors;
  setErrors: React.Dispatch<React.SetStateAction<TErrors>>;
  onChange: (params: { name: keyof TForm; value: any }) => void;
  onOpenInfoModal: (id: "" | "1" | "2" | "3" | "4") => void;
  onClear: () => void;
  onContinue: () => void;
  continueDisabled: boolean;
};

export const EosForm: React.FC<Props> = ({
  form,
  errors,
  setErrors,
  onChange,
  onOpenInfoModal,
  onClear,
  onContinue,
  continueDisabled,
}) => {
  // Validate all fields we care about; optionally treat some as required.
  const validateAll = () => {
    const next: TErrors = {};

    // temperature required + 36..40
    if (form.temperature === undefined || outOfRange(form.temperature, 36, 40)) {
      next.temperature = true;
    }

    // weeks required + 34..43
    if (
      form.pregnancyLengthWeeks === undefined ||
      outOfRange(form.pregnancyLengthWeeks, 34, 43)
    ) {
      next.pregnancyLengthWeeks = true;
    }

    // days optional, but if filled => 0..6
    if (
      form.pregnancyLengthDays !== undefined &&
      outOfRange(form.pregnancyLengthDays, 0, 6)
    ) {
      next.pregnancyLengthDays = true;
    }

    // rom required + 0..150
    if (form.rom === undefined || outOfRange(form.rom, 0, 150)) {
      next.rom = true;
    }

    // gbs required
    if (!form.gbs) {
      next.gbs = true;
    }

    setErrors(next);
    return next;
  };

  // Update a single error flag on each change.
  const setFieldError = (name: keyof TForm, hasError: boolean) => {
    setErrors((prev) => ({ ...prev, [name]: hasError }));
  };

  // --- Delayed validation timers (SPA UX: don't flash error while user is mid-typing) ---
  const temperatureTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const weeksTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    // Cleanup timers on unmount
    return () => {
      if (temperatureTimerRef.current) clearTimeout(temperatureTimerRef.current);
      if (weeksTimerRef.current) clearTimeout(weeksTimerRef.current);
    };
  }, []);
  // -------------------------------------------------------------------------------

  return (
    <div className="eos-form">
      <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: "0" }}>
        <tbody>
          {/* ================= Temperature ================= */}
          <tr>
            <td style={{ width: "25%", direction: "rtl", verticalAlign: "middle" }}>
              <label className="grid-label" style={labelStyle}>
                חום אימהי מקסימלי
              </label>
            </td>
            <td style={{ width: "75%", direction: "rtl" }} colSpan={3}>
              <Input
                type="number"
                data-testid="temperature"
                value={form.temperature !== undefined ? String(form.temperature) : ""}
                placeholder="צלזיוס"
                min={36}
                max={40}
                onInvalid={suppressNativeInvalid}
                onChange={(e) => {
                  e.currentTarget.setCustomValidity("");

                  // Clear previous timer on every keystroke
                  if (temperatureTimerRef.current) {
                    clearTimeout(temperatureTimerRef.current);
                    temperatureTimerRef.current = null;
                  }

                  const raw = e.target.value;
                  const value = raw === "" ? undefined : Number(raw);

                  onChange({ name: "temperature", value });

                  // Don't show error while empty
                  if (raw === "") {
                    setFieldError("temperature", false);
                    return;
                  }

                  // If valid now, clear error immediately
                  const isValidNow =
                    value !== undefined &&
                    !Number.isNaN(value) &&
                    value >= 36 &&
                    value <= 40;

                  if (isValidNow) {
                    setFieldError("temperature", false);
                    return;
                  }

                  // Otherwise delay showing error (user might still be typing)
                  temperatureTimerRef.current = setTimeout(() => {
                    const v = value;
                    const hasError = v === undefined || Number.isNaN(v) || v < 36 || v > 40;
                    setFieldError("temperature", hasError);
                  }, 1000);
                }}
                onBlur={() => {
                  // On blur: show error immediately if invalid
                  if (temperatureTimerRef.current) {
                    clearTimeout(temperatureTimerRef.current);
                    temperatureTimerRef.current = null;
                  }

                  const v = form.temperature;
                  const hasError = v === undefined || Number.isNaN(v) || v < 36 || v > 40;
                  setFieldError("temperature", hasError);
                }}
                classNames={{
                  inputWrapper: `
                    bg-white border rounded-sm
                    hover:border-blue-600 focus-within:border-blue-700
                    ${errors.temperature ? "input-error-border" : "border-blue-500"}
                  `,
                  input: "bg-white text-right",
                }}
              />
            </td>
          </tr>
          <tr>
            <td />
            <td colSpan={3} style={{ direction: "rtl" }}>
              <div style={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
                <FieldError
                  show={!!errors.temperature}
                  testId="temperature-error"
                  message="יש לבחור ערך בין 36-40"
                />
              </div>
            </td>
          </tr>

          {/* ================= Pregnancy length ================= */}
          <tr>
            <td style={{ width: "25%", direction: "rtl", verticalAlign: "middle" }}>
              <label className="grid-label" style={labelStyle}>
                משך הריון
              </label>
            </td>

            <td style={{ width: "35%", direction: "rtl", minWidth: 0 }}>
              <Input
                type="number"
                data-testid="pregnancyLengthWeeks"
                value={
                  form.pregnancyLengthWeeks !== undefined
                    ? String(form.pregnancyLengthWeeks)
                    : ""
                }
                placeholder="שבוע"
                min={34}
                max={43}
                onInvalid={suppressNativeInvalid}
                onChange={(e) => {
                  e.currentTarget.setCustomValidity("");

                  // Clear previous timer on every keystroke
                  if (weeksTimerRef.current) {
                    clearTimeout(weeksTimerRef.current);
                    weeksTimerRef.current = null;
                  }

                  const raw = e.target.value;
                  const value = raw === "" ? undefined : Number(raw);

                  onChange({ name: "pregnancyLengthWeeks", value });

                  // Don't show error while empty
                  if (raw === "") {
                    setFieldError("pregnancyLengthWeeks", false);
                    return;
                  }

                  // If valid now, clear error immediately
                  const isValidNow =
                    value !== undefined &&
                    !Number.isNaN(value) &&
                    value >= 34 &&
                    value <= 43;

                  if (isValidNow) {
                    setFieldError("pregnancyLengthWeeks", false);
                    return;
                  }

                  // Otherwise delay showing error (user might still be typing)
                  weeksTimerRef.current = setTimeout(() => {
                    const v = value;
                    const hasError = v === undefined || Number.isNaN(v) || v < 34 || v > 43;
                    setFieldError("pregnancyLengthWeeks", hasError);
                  }, 1000);
                }}
                onBlur={() => {
                  // On blur: show error immediately if invalid
                  if (weeksTimerRef.current) {
                    clearTimeout(weeksTimerRef.current);
                    weeksTimerRef.current = null;
                  }

                  const v = form.pregnancyLengthWeeks;
                  const hasError = v === undefined || Number.isNaN(v) || v < 34 || v > 43;
                  setFieldError("pregnancyLengthWeeks", hasError);
                }}
                classNames={{
                  inputWrapper: `
                    bg-white border rounded-sm
                    hover:border-blue-600 focus-within:border-blue-700
                    ${errors.pregnancyLengthWeeks ? "input-error-border" : "border-blue-500"}
                  `,
                  input: "bg-white text-right",
                }}
              />
            </td>

            <td style={{ width: "5%", direction: "rtl" }} />

            <td style={{ width: "35%", direction: "rtl", minWidth: 0 }}>
              <Input
                type="number"
                data-testid="pregnancyLengthDays"
                value={
                  form.pregnancyLengthDays !== undefined
                    ? String(form.pregnancyLengthDays)
                    : ""
                }
                placeholder="+ ימים"
                min={0}
                max={6}
                onInvalid={suppressNativeInvalid}
                onChange={(e) => {
                  e.currentTarget.setCustomValidity("");
                  const value = Number(e.target.value);

                  onChange({ name: "pregnancyLengthDays", value });

                  const hasError =
                    e.target.value !== "" && (Number.isNaN(value) || value < 0 || value > 6);
                  setFieldError("pregnancyLengthDays", hasError);
                }}
                onBlur={() => {
                  const v = form.pregnancyLengthDays;
                  const hasError = v !== undefined && (Number.isNaN(v) || v < 0 || v > 6);
                  setFieldError("pregnancyLengthDays", hasError);
                }}
                classNames={{
                  inputWrapper: `
                    bg-white border rounded-sm
                    hover:border-blue-600 focus-within:border-blue-700
                    ${errors.pregnancyLengthDays ? "input-error-border" : "border-blue-500"}
                  `,
                  input: "bg-white text-right",
                }}
              />
            </td>
          </tr>

          <tr>
            <td />
            <td style={{ direction: "rtl" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  width: "100%",
                  marginTop: 3,
                }}
              >
                <FieldError
                  show={!!errors.pregnancyLengthWeeks}
                  testId="pregnancyWeeks-error"
                  message="ערך בין 34-43"
                />
              </div>
            </td>
            <td style={{ width: "5%", direction: "rtl" }} />
            <td style={{ direction: "rtl" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  width: "100%",
                  marginTop: 3,
                }}
              >
                <FieldError
                  show={!!errors.pregnancyLengthDays}
                  testId="pregnancyDays-error"
                  message="ערך בין 0-6"
                />
              </div>
            </td>
          </tr>

          {/* ================= ROM ================= */}
          <tr>
            <td style={{ width: "25%", direction: "rtl", verticalAlign: "middle" }}>
              <label className="grid-label" style={labelStyle}>
                משך פקיעת הקרומים
              </label>
            </td>

            <td style={{ direction: "rtl" }} colSpan={3}>
              <Input
                type="number"
                data-testid="rom"
                value={form.rom !== undefined ? String(form.rom) : ""}
                placeholder="בשעות"
                min={0}
                max={150}
                onInvalid={suppressNativeInvalid}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  onChange({ name: "rom", value });

                  const hasError =
                    e.target.value === "" || Number.isNaN(value) || value < 0 || value > 150;
                  setFieldError("rom", hasError);
                }}
                onBlur={() => {
                  const v = form.rom;
                  const hasError = v === undefined || Number.isNaN(v) || v < 0 || v > 150;
                  setFieldError("rom", hasError);
                }}
                classNames={{
                  inputWrapper: `
                    bg-white border rounded-sm
                    hover:border-blue-600 focus-within:border-blue-700
                    ${errors.rom ? "input-error-border" : "border-blue-500"}
                  `,
                  input: "bg-white text-right",
                }}
              />
            </td>
          </tr>

          <tr>
            <td />
            <td colSpan={3} style={{ direction: "rtl" }}>
              <div style={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
                <FieldError show={!!errors.rom} testId="rom-error" message="יש לבחור ערך בין 0-150" />
              </div>
            </td>
          </tr>

          {/* ================= GBS ================= */}
          <tr>
            <td style={{ direction: "rtl", verticalAlign: "middle" }}>
              <label className="grid-label" style={labelStyle}>
                סטטוס GBS
              </label>
            </td>

            <td style={{ direction: "rtl" }} colSpan={3}>
              {/* Using a stable test-id on the Select root for e2e. */}
              <Select
                data-testid="gbs"
                aria-label="gbs"
                labelPlacement="outside"
                placeholder="בחירה"
                selectionMode="single"
                selectedKeys={form.gbs ? new Set([form.gbs]) : new Set()}
                onSelectionChange={(keys) => {
                  const value = Array.from(keys)[0] as NonNullable<TForm["gbs"]> | undefined;

                  onChange({ name: "gbs", value });

                  // Clear error when user selected something valid
                  setFieldError("gbs", !value);
                }}
                className="w-full"
                popoverProps={{
                  // Avoid immediate dismiss when scroll fires on open (React Aria useCloseOnScroll).
                  shouldCloseOnScroll: false,
                  // Ensure popover renders in a portal to avoid clipping by parent overflow
                  portalContainer: typeof document !== "undefined" ? document.body : undefined,
                }}
                classNames={{
                  trigger: `
                      min-h-10 w-full bg-white border rounded-lg shadow-sm
                      hover:bg-white hover:border-blue-600
                      focus:bg-white focus-within:border-blue-700
                      ${errors.gbs ? "input-error-border" : "border-blue-500"}
                    `,
                  value: "whitespace-nowrap overflow-hidden text-ellipsis text-right",
                  popoverContent: "min-w-0 max-w-[min(90dvw,250px)] z-50",
                  listbox: "py-1 max-h-[40vh] overflow-auto",
                }}
              >
                {/* Using data-testid on options so tests can select without text. */}
                <SelectItem key="positive" data-testid="gbs-option-positive">
                  חיובי
                </SelectItem>
                <SelectItem key="negative" data-testid="gbs-option-negative">
                  שלילי
                </SelectItem>
                <SelectItem key="unknown" data-testid="gbs-option-unknown">
                  לא ידוע
                </SelectItem>
              </Select>
            </td>
          </tr>

          <tr>
            <td />
            <td colSpan={3} style={{ direction: "rtl" }}>
              <FieldError show={!!errors.gbs} testId="gbs-error" message="יש לבחור" />
            </td>
          </tr>
        </tbody>
      </table>

      <div style={{ display: "flex", alignItems: "center", marginBottom: "6px" }}>
        <div
          style={{
            color: "#0C3058",
            fontWeight: 700,
            fontSize: "1.3rem",
            lineHeight: 1.3,
            textAlign: "right",
          }}
        >
          סוג טיפול אנטיביוטי בלידה:
        </div>

        <FontAwesomeIcon
          icon={faCircleInfo}
          style={{ marginInlineStart: "auto", cursor: "pointer" }}
          onClick={() => onOpenInfoModal("4")}
        />
      </div>

      <div className="form-grid">
        <div className="grid-input">
          <AntibioticTreatmentSelect
            value={form.antibioticTreatment}
            onChange={(v) => onChange({ name: "antibioticTreatment", value: v })}
          />
        </div>
      </div>

      {/* Buttons: Clear + Continue */}
      <div style={{ display: "flex", gap: "10%", marginTop: "10px", flexWrap: "wrap" }}>
        <Button
          data-testid="calculator-calc-btn"
          className="btn-custom-green"
          type="button"
          data-track-id="eos_continue"
          style={{
            backgroundColor: "#103C6E",
            color: "white",
            width: "45%",
            minWidth: "130px",
            height: "48px",
            fontSize: "1.25rem",
            fontWeight: "500",
            borderRadius: "4px",
          }}
          onClick={() => {
            const next = validateAll();
            const hasAnyError = Object.values(next).some(Boolean);
            if (!hasAnyError) onContinue();
          }}
          disabled={continueDisabled}
        >
          המשך
        </Button>

        <Button
          data-testid="calculator-clear-btn"
          type="button"
          className="btn-custom-green"
          data-track-id="eos_clean"
          style={{
            backgroundColor: "white",
            color: "#103C3058",
            minWidth: "130px",
            width: "45%",
            height: "48px",
            fontSize: "1.25rem",
            fontWeight: "500",
            border: "1px solid #103C6E",
            borderRadius: "4px",
          }}
          onClick={onClear}
        >
          נקה
        </Button>
      </div>
    </div>
  );
};